<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: 'left', // 'left' or 'right'
    validator: value => ['left', 'right'].includes(value),
  },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();

// Common WhatsApp reactions
const reactions = [
  { emoji: '👍', label: 'LIKE', key: 'CONVERSATION.REACTIONS.LIKE' },
  { emoji: '❤️', label: 'LOVE', key: 'CONVERSATION.REACTIONS.LOVE' },
  { emoji: '😂', label: 'LAUGH', key: 'CONVERSATION.REACTIONS.LAUGH' },
  { emoji: '😮', label: 'SURPRISED', key: 'CONVERSATION.REACTIONS.SURPRISED' },
  { emoji: '😢', label: 'SAD', key: 'CONVERSATION.REACTIONS.SAD' },
  { emoji: '🙏', label: 'THANKS', key: 'CONVERSATION.REACTIONS.THANKS' },
  { emoji: '🔥', label: 'FIRE', key: 'CONVERSATION.REACTIONS.FIRE' },
  { emoji: '✨', label: 'SPARKLE', key: 'CONVERSATION.REACTIONS.SPARKLE' },
];

const hoveredReaction = ref(null);

const positionClass = computed(() => {
  return props.position === 'left' ? 'left-0' : 'right-0';
});

function handleClose() {
  hoveredReaction.value = null;
  emit('close');
}

function handleSelect(reaction) {
  emit('select', reaction.emoji);
  handleClose();
}

function handleMouseEnter(reaction) {
  hoveredReaction.value = reaction.emoji;
}

function handleMouseLeave() {
  hoveredReaction.value = null;
}
</script>

<template>
  <Transition
    enter-active-class="transition ease-out duration-100"
    enter-from-class="transform opacity-0 scale-95"
    enter-to-class="transform opacity-100 scale-100"
    leave-active-class="transition ease-in duration-75"
    leave-from-class="transform opacity-100 scale-100"
    leave-to-class="transform opacity-0 scale-95"
  >
    <div
      v-if="show"
      class="absolute z-50 bottom-full mb-2"
      :class="positionClass"
      @mouseleave="handleMouseLeave"
    >
      <div
        class="flex items-center gap-1 px-2 py-1.5 bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg"
      >
        <button
          v-for="reaction in reactions"
          :key="reaction.emoji"
          type="button"
          class="relative flex items-center justify-center w-8 h-8 rounded-md hover:bg-n-alpha-3 transition-all duration-150"
          :class="{
            'bg-n-alpha-2 scale-110': hoveredReaction === reaction.emoji,
          }"
          :title="t(reaction.key)"
          @click="handleSelect(reaction)"
          @mouseenter="handleMouseEnter(reaction)"
        >
          <span class="text-lg leading-none">{{ reaction.emoji }}</span>
        </button>
      </div>

      <!-- Triangle pointer -->
      <div
        class="absolute top-full w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-n-slate-6"
        :class="position === 'left' ? 'left-4' : 'right-4'"
      >
        <div
          class="absolute -top-[7px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-n-slate-1"
        />
      </div>
    </div>
  </Transition>
</template>
